import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useWhatsAppAuth } from "../WhatsAppAuthProvider/WhatsAppAuth.context";
import { initializeWhatsAppSocket } from "./socket.client";
import {
  ISocket,
  IWhatsAppSocketContextState,
  IWhatsAppSocketResponse,
} from "./WhatsAppSocket.types";

// ─── Context ─────────────────────────────────────────────────────

const WhatsAppSocketContext = createContext<IWhatsAppSocketContextState>({
  socket: null,
});

// ─── Provider ────────────────────────────────────────────────────

export const WhatsAppSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, outlet } = useWhatsAppAuth();
  const [socket, setSocket] = useState<ISocket>(null);
  const joinedOutletRef = useRef<string | null>(null);

  const userUuid = user.user_uuid;
  const outletUuid = outlet.company_outlet_uuid;

  // Connect once the user identity is known; join the company outlet room.
  useEffect(() => {
    if (!userUuid) return;

    let active = true;
    let s: ReturnType<typeof initializeWhatsAppSocket> | null = null;

    try {
      s = initializeWhatsAppSocket(userUuid);

      s.on("connect", () => {
        if (!active || !s) return;
        setSocket(s);
        console.log("✅ Socket connected:", s.connected, s.id);

        
        if (outletUuid && joinedOutletRef.current !== outletUuid) {
          s.emit("join_outlet", outletUuid);
          joinedOutletRef.current = outletUuid;
          console.log(`User ${userUuid} joined OUTLET_${outletUuid}`);
        }
      });

      s.on("connect_error", (err) => {
        console.error("WhatsApp socket connection error:", err);
        setSocket(null);
      });

      s.on("disconnect", () => {
        setSocket(null);
        joinedOutletRef.current = null;
      });
    } catch (err) {
      console.error("Failed to initialize WhatsApp socket:", err);
      setSocket(null);
    }

    return () => {
      active = false;
      if (s) s.disconnect();
      joinedOutletRef.current = null;
    };
     
  }, [userUuid]);

  // Re-join when the company outlet changes after connecting.
  useEffect(() => {
    if (!socket || !socket.connected) return;
    if (!outletUuid || joinedOutletRef.current === outletUuid) return;
    socket.emit(joinedOutletRef.current ? "switch_outlet" : "join_outlet", outletUuid);
    joinedOutletRef.current = outletUuid;
  }, [socket, outletUuid]);

  return (
    <WhatsAppSocketContext.Provider value={{ socket }}>
      {children}
    </WhatsAppSocketContext.Provider>
  );
};

// ─── Consumer Hooks ──────────────────────────────────────────────

export const useWhatsAppSocketContext = (): IWhatsAppSocketContextState => {
  return useContext(WhatsAppSocketContext);
};

/**
 * Subscribe to WhatsApp socket message events.
 * Keeps its own local state so only the consuming component re-renders.
 */
export const useWhatsappMessageReceivedSocket = (): IWhatsAppSocketResponse | null => {
  const { socket } = useWhatsAppSocketContext();
  const [socketResponse, setSocketResponse] = useState<IWhatsAppSocketResponse | null>(null);

  useEffect(() => {
    if (!socket) return;

    const onReceived = (data: IWhatsAppSocketResponse) => {
      if (data.conversation.status === "RECEIVED") {
        setSocketResponse({ conversation: data.conversation, status: "MESSAGE_RECEIVED" });
      }
    };
    const onSent = (data: IWhatsAppSocketResponse) => {
      setSocketResponse({ conversation: data.conversation, status: "MESSAGE_SENT" });
    };
    const onStatus = (data: IWhatsAppSocketResponse) => {
      setSocketResponse({ conversation: data.conversation, status: "MESSAGE_STATUS_RECEIVED" });
    };

    socket.on("MESSAGE_RECEIVED", onReceived);
    socket.on("SEND_MESSAGE", onSent);
    socket.on("MESSAGE_STATUS_RECEIVED", onStatus);

    return () => {
      socket.off("MESSAGE_RECEIVED", onReceived);
      socket.off("SEND_MESSAGE", onSent);
      socket.off("MESSAGE_STATUS_RECEIVED", onStatus);
    };
  }, [socket]);

  return socketResponse;
};
